import MaterialCommunityIcons from "@expo/vector-icons/MaterialCommunityIcons";
import { Circle, ClipPath, Defs, G, Path, Rect, Svg } from "react-native-svg";
import { styleConstants as C } from "~/style/styleConstants";
import { styleConstantsV2 } from "~/style/styleConstantsV2";
import type { TextStyle, ViewStyle } from "react-native";

export interface IconProps {
  name: React.ComponentProps<typeof MaterialCommunityIcons>["name"] | IconName;
  color?: string;
  size?: number;
  style?: ViewStyle & TextStyle;
}

// Include only those icons here which can accept size, color etc.
// For static icons, please add them to /src/assets/static-icons.
export type IconName =
  | "down-arrow"
  | "check"
  | "close"
  | "left-arrow"
  | "settings"
  | "calendar"
  | "chevron-right"
  | "error-warning"
  | "edit"
  | "search"
  | "info"
  | "location"
  | "check-circle"
  | "cross-circle";

export function Icon({
  name,
  color = C.colorTextLight,
  size = 28,
  ...props
}: IconProps) {
  switch (name) {
    case "lock-outline": {
      return (
        <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
          <G id="Icons">
            <Path
              id="Vector"
              d="M8.75 9.25V6.5C8.75 4.7 10.2 3.25 12 3.25C13.8 3.25 15.25 4.7 15.25 6.5V9.25"
              stroke={color}
              strokeWidth={1.5}
              strokeMiterlimit={10}
            />
            <Path
              id="Vector_2"
              d="M12 17C12.8284 17 13.5 16.3284 13.5 15.5C13.5 14.6716 12.8284 14 12 14C11.1716 14 10.5 14.6716 10.5 15.5C10.5 16.3284 11.1716 17 12 17Z"
              fill={color}
            />
            <Path
              id="Vector_3"
              d="M12 15.5V18.5"
              stroke={color}
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <Path
              id="Vector_4"
              d="M12.0003 9.25026H17.7503C18.6003 9.25026 19.2503 9.90026 19.2503 10.7503V20.2503C19.2503 21.1003 18.6003 21.7503 17.7503 21.7503H6.25026C6.15676 21.7518 5.61526 21.7528 5.18126 21.3193C4.74726 20.8853 4.74826 20.3443 4.75026 20.2503V10.7503C4.74876 10.6568 4.74776 10.1153 5.18176 9.68126C5.61576 9.24726 6.15676 9.24826 6.25026 9.25026H12.0003Z"
              stroke={color}
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </G>
        </Svg>
      );
    }
    case "cross-circle": {
      const width = size;
      const aspectRatio = 24 / 25;
      const height = width * aspectRatio;

      return (
        <Svg width={width} height={height} viewBox="0 0 24 25" fill="none">
          <G clipPath="url(#clip0_18240_2736)">
            <Path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M12 0.5C5.38 0.5 0 5.88 0 12.5C0 19.12 5.38 24.5 12 24.5C18.62 24.5 24 19.12 24 12.5C24 5.88 18.63 0.5 12 0.5Z"
              fill={styleConstantsV2.colorError}
            />
            <Path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M9.39 8.74C9.07 8.42 8.56 8.42 8.24 8.74C7.92 9.06 7.92 9.57 8.24 9.89L11.02 12.67L8.24 15.45C7.92 15.77 7.92 16.28 8.24 16.6C8.56 16.92 9.07 16.92 9.39 16.6L12.17 13.82L14.95 16.6C15.27 16.92 15.78 16.92 16.1 16.6C16.42 16.28 16.42 15.77 16.1 15.45L13.32 12.67L16.1 9.89C16.42 9.57 16.42 9.06 16.1 8.74C15.78 8.42 15.27 8.42 14.95 8.74L12.17 11.52L9.39 8.74Z"
              fill="white"
            />
          </G>
          <Defs>
            <ClipPath id="clip0_18240_2736">
              <Rect
                width={size}
                height={size}
                fill={styleConstantsV2.colorWhite}
                transform="translate(0 0.5)"
              />
            </ClipPath>
          </Defs>
        </Svg>
      );
    }
    case "check-circle": {
      const width = size;
      const aspectRatio = 24 / 25;
      const height = width * aspectRatio;

      return (
        <Svg width={width} height={height} viewBox="0 0 24 25" fill="none">
          <G clipPath="url(#clip0_18240_2708)">
            <Path
              d="M12 23.75C18.2132 23.75 23.25 18.7132 23.25 12.5C23.25 6.2868 18.2132 1.25 12 1.25C5.7868 1.25 0.75 6.2868 0.75 12.5C0.75 18.7132 5.7868 23.75 12 23.75Z"
              fill={styleConstantsV2.colorSuccess}
            />
            <Path
              d="M12 2C17.79 2 22.5 6.71 22.5 12.5C22.5 18.29 17.79 23 12 23C6.21 23 1.5 18.29 1.5 12.5C1.5 6.71 6.21 2 12 2ZM12 0.5C5.37 0.5 0 5.87 0 12.5C0 19.13 5.37 24.5 12 24.5C18.63 24.5 24 19.13 24 12.5C24 5.87 18.63 0.5 12 0.5Z"
              fill={styleConstantsV2.colorSuccess}
            />
            <Path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M7.52107 12.5C7.23107 12.79 7.22107 13.27 7.52107 13.56L9.78855 15.8475C9.92855 15.9875 10.1185 16.0675 10.3185 16.0675C10.5185 16.0675 10.7085 15.9875 10.8485 15.8475L16.3885 10.2775C16.6785 9.9875 16.6785 9.5075 16.3885 9.2175C16.0985 8.9275 15.6185 8.9275 15.3285 9.2175L10.3185 14.2575L8.58107 12.5C8.29107 12.21 7.81107 12.2 7.52107 12.5Z"
              fill={styleConstantsV2.colorWhite}
            />
          </G>
          <Defs>
            <ClipPath id="clip0_18240_2708">
              <Rect
                width={size}
                height={size}
                fill="white"
                transform="translate(0 0.5)"
              />
            </ClipPath>
          </Defs>
        </Svg>
      );
    }
    case "location": {
      return (
        <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
          <Path
            d="M12.5 12.75C13.7426 12.75 14.75 11.7426 14.75 10.5C14.75 9.25736 13.7426 8.25 12.5 8.25C11.2574 8.25 10.25 9.25736 10.25 10.5C10.25 11.7426 11.2574 12.75 12.5 12.75Z"
            stroke={color}
            strokeWidth={1.5}
          />
          <Path
            d="M18.4004 15.55C19.5504 14.2 20.2504 12.45 20.2504 10.5C20.2504 6.2 16.8004 2.75 12.5004 2.75C11.0004 2.75 9.60039 3.2 8.40039 3.9"
            stroke={color}
            strokeWidth={1.5}
            strokeLinecap="round"
          />
          <Path
            d="M5.7 6.7998C5.1 7.8998 4.75 9.1498 4.75 10.4998C4.75 12.3998 5.45 14.1498 6.6 15.5498C6.6 15.5498 10.3 19.7498 11.45 20.8498C12.05 21.3998 12.95 21.3998 13.5 20.8498C14.25 20.1498 15.1 19.1998 16.2 17.9498"
            stroke={color}
            strokeWidth={1.5}
            strokeLinecap="round"
          />
        </Svg>
      );
    }
    case "info": {
      return (
        <Svg width={size} height={size} viewBox="0 0 32 32" fill="none">
          <Circle
            cx={16}
            cy={16}
            r={15}
            fill="transparent"
            stroke="transparent"
            strokeWidth={2}
          />
          <Path
            d="M15.9047 9.80944C16.9567 9.80944 17.8094 8.95666 17.8094 7.90472C17.8094 6.85277 16.9567 6 15.9047 6C14.8528 6 14 6.85277 14 7.90472C14 8.95666 14.8528 9.80944 15.9047 9.80944Z"
            fill="#181818"
          />
          <Path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M15.9051 12.6669C16.6941 12.6669 17.3336 13.3064 17.3336 14.0954V24.5714C17.3336 25.3603 16.6941 25.9999 15.9051 25.9999C15.1161 25.9999 14.4766 25.3603 14.4766 24.5714V14.0954C14.4766 13.3064 15.1161 12.6669 15.9051 12.6669Z"
            fill="#181818"
          />
        </Svg>
      );
    }
    case "search": {
      return (
        <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
          <Path
            d="M16.1996 13.5996L20.2496 17.6496C21.0996 18.4996 21.0996 19.8996 20.2496 20.7496C19.3996 21.5996 17.9996 21.5996 17.1496 20.7496L14.9996 18.5996C14.4286 18.0286 13.8576 17.4576 13.2871 16.8871"
            stroke={color}
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <Path
            d="M4.00022 12.9996C3.78772 12.3881 3.15422 10.3186 4.08922 8.08414C4.23622 7.73264 4.67622 6.82414 5.50022 5.99964C7.40022 4.09964 10.2502 3.59964 12.6002 4.54964C13.3137 4.84814 14.6977 5.55414 15.6502 7.04964C17.1377 9.38514 17.1622 13.0026 14.8002 15.3496C12.2137 17.9196 8.20472 17.5346 5.95022 15.7496C4.80922 14.8461 4.26072 13.6971 4.00022 12.9996Z"
            stroke={color}
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </Svg>
      );
    }
    case "edit": {
      const width = size;
      const aspectRatio = 24 / 25;
      const height = width * aspectRatio;

      return (
        <Svg width={width} height={height} viewBox="0 0 24 25" fill="none">
          <Path
            d="M16.25 12.1499C13.8165 14.5664 11.3835 16.9834 8.95 19.3999C8.75 19.5999 8.55 19.6999 8.3 19.7999L3.25 21.2499L4.7 16.1999C4.75 15.9499 4.9 15.7499 5.1 15.5499C5.7165 14.9334 6.3335 14.3164 6.95 13.6999C7.9435 12.7284 8.715 11.9189 9.2 11.3999C10.269 10.2564 12.196 8.35393 16.05 4.54993C16.1995 4.40193 16.5455 4.06843 17.093 3.89393C17.9905 3.60843 19.3215 3.78543 20.1 4.74993C20.939 5.78893 20.872 7.41393 19.85 8.49993C18.65 9.71643 17.45 10.9334 16.25 12.1499Z"
            stroke={color}
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <Path
            d="M14.5498 6.05005C15.7888 7.28905 17.0278 8.52805 18.2668 9.76705"
            stroke={color}
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </Svg>
      );
    }
    case "error-warning": {
      return (
        <Svg width={size} height={size} viewBox="0 0 144 144" fill="none">
          <Path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M83.757 22.1557C78.6788 12.5611 65.2329 12.8969 59.9929 22.0667L59.9627 22.1205L42.8627 53.0205C41.6593 55.195 42.4466 57.9333 44.6211 59.1367C46.7956 60.34 49.534 59.5528 50.7373 57.3783L67.8202 26.5092C69.7772 23.121 74.2728 23.4455 75.8138 26.3874C75.8316 26.4215 75.8499 26.4554 75.8687 26.489L102.269 73.889C103.478 76.0602 106.218 76.84 108.39 75.6307C110.561 74.4214 111.341 71.681 110.131 69.5098L83.757 22.1557Z"
            fill={color}
          />
          <Path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M39.6826 68.3644C41.856 69.5698 42.6407 72.3088 41.4353 74.4822L21.643 110.168C21.6419 110.17 21.6408 110.172 21.6397 110.174C19.9106 113.328 22.1988 117 25.5 117H118.5C122.081 117 124.15 113.163 122.441 110.315C122.413 110.267 122.385 110.219 122.358 110.17L110.958 89.4705C109.759 87.2935 110.552 84.5568 112.729 83.3579C114.906 82.159 117.643 82.9518 118.842 85.1288L130.205 105.763C135.61 114.9 128.689 126 118.5 126H25.5C15.0032 126 8.89217 114.676 13.757 105.831L13.7647 105.817L13.7647 105.817L33.5647 70.1171C34.7701 67.9437 37.5092 67.159 39.6826 68.3644Z"
            fill={color}
          />
          <Path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M72 48C74.4853 48 76.5 50.0147 76.5 52.5V82.5C76.5 84.9853 74.4853 87 72 87C69.5147 87 67.5 84.9853 67.5 82.5V52.5C67.5 50.0147 69.5147 48 72 48Z"
            fill={color}
          />
          <Path
            d="M72 108C75.3137 108 78 105.314 78 102C78 98.6863 75.3137 96 72 96C68.6863 96 66 98.6863 66 102C66 105.314 68.6863 108 72 108Z"
            fill={color}
          />
        </Svg>
      );
    }
    case "chevron-right": {
      return (
        <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
          <G clipPath="url(#clip0_11249_1566)">
            <Path
              d="M8.42676 5.26251C8.17788 5.45501 8.02676 5.72626 8.00899 6.02376C7.98232 6.3125 8.08899 6.60126 8.30232 6.82001L13.2179 12L8.29343 17.1888C8.0801 17.4075 7.98232 17.6875 8.0001 17.985C8.02676 18.2825 8.17788 18.5538 8.42676 18.7463C8.92454 19.1313 9.6801 19.0788 10.1068 18.6238L15.7068 12.7175C15.9023 12.5163 16.0001 12.2538 16.0001 12C16.0001 11.7463 15.9023 11.4838 15.7068 11.2825L10.1068 5.37625C9.6801 4.92126 8.92454 4.86876 8.42676 5.25375L8.42676 5.26251Z"
              fill={color}
            />
          </G>
          <Defs>
            <ClipPath id="clip0_11249_1566">
              <Rect
                width={14}
                height={8}
                fill="white"
                transform="translate(8 19) rotate(-90)"
              />
            </ClipPath>
          </Defs>
        </Svg>
      );
    }
    case "chevron-left": {
      return (
        <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
          <G clipPath="url(#clip0_11249_1563)">
            <Path
              d="M15.5732 18.7375C15.8221 18.545 15.9732 18.2737 15.991 17.9762C16.0177 17.6875 15.911 17.3987 15.6977 17.18L10.7821 12L15.7066 6.81124C15.9199 6.59249 16.0177 6.3125 15.9999 6.015C15.9732 5.7175 15.8221 5.44625 15.5732 5.25375C15.0755 4.86875 14.3199 4.92125 13.8932 5.37625L8.29323 11.2825C8.09768 11.4837 7.9999 11.7462 7.9999 12C7.9999 12.2537 8.09768 12.5162 8.29323 12.7175L13.8932 18.6237C14.3199 19.0787 15.0755 19.1312 15.5732 18.7462L15.5732 18.7375Z"
              fill={color}
            />
          </G>
          <Defs>
            <ClipPath id="clip0_11249_1563">
              <Rect
                width={14}
                height={8}
                fill="white"
                transform="translate(16 5) rotate(90)"
              />
            </ClipPath>
          </Defs>
        </Svg>
      );
    }
    case "calendar": {
      return (
        <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
          <Path
            d="M6.25 3C4.46403 3 3 4.46403 3 6.25L3 17.75C3 19.536 4.46403 21 6.25 21L17.75 21C19.536 21 21 19.536 21 17.75L21 6.25C21 4.46403 19.536 3 17.75 3L6.25 3ZM6.25 4.5L17.75 4.5C18.725 4.5 19.5 5.27497 19.5 6.25V7L4.5 7L4.5 6.25C4.5 5.27497 5.27497 4.5 6.25 4.5ZM4.5 8.5L19.5 8.5L19.5 17.75C19.5 18.725 18.725 19.5 17.75 19.5L6.25 19.5C5.27497 19.5 4.5 18.725 4.5 17.75L4.5 8.5ZM7.75 10.5C7.41848 10.5 7.10054 10.6317 6.86612 10.8661C6.6317 11.1005 6.5 11.4185 6.5 11.75C6.5 12.0815 6.6317 12.3995 6.86612 12.6339C7.10054 12.8683 7.41848 13 7.75 13C8.08152 13 8.39946 12.8683 8.63388 12.6339C8.8683 12.3995 9 12.0815 9 11.75C9 11.4185 8.8683 11.1005 8.63388 10.8661C8.39946 10.6317 8.08152 10.5 7.75 10.5ZM12 10.5C11.6685 10.5 11.3505 10.6317 11.1161 10.8661C10.8817 11.1005 10.75 11.4185 10.75 11.75C10.75 12.0815 10.8817 12.3995 11.1161 12.6339C11.3505 12.8683 11.6685 13 12 13C12.3315 13 12.6495 12.8683 12.8839 12.6339C13.1183 12.3995 13.25 12.0815 13.25 11.75C13.25 11.4185 13.1183 11.1005 12.8839 10.8661C12.6495 10.6317 12.3315 10.5 12 10.5ZM16.25 10.5C15.9185 10.5 15.6005 10.6317 15.3661 10.8661C15.1317 11.1005 15 11.4185 15 11.75C15 12.0815 15.1317 12.3995 15.3661 12.6339C15.6005 12.8683 15.9185 13 16.25 13C16.5815 13 16.8995 12.8683 17.1339 12.6339C17.3683 12.3995 17.5 12.0815 17.5 11.75C17.5 11.4185 17.3683 11.1005 17.1339 10.8661C16.8995 10.6317 16.5815 10.5 16.25 10.5ZM7.75 15C7.41848 15 7.10054 15.1317 6.86612 15.3661C6.6317 15.6005 6.5 15.9185 6.5 16.25C6.5 16.5815 6.6317 16.8995 6.86612 17.1339C7.10054 17.3683 7.41848 17.5 7.75 17.5C8.08152 17.5 8.39946 17.3683 8.63388 17.1339C8.8683 16.8995 9 16.5815 9 16.25C9 15.9185 8.8683 15.6005 8.63388 15.3661C8.39946 15.1317 8.08152 15 7.75 15ZM12 15C11.6685 15 11.3505 15.1317 11.1161 15.3661C10.8817 15.6005 10.75 15.9185 10.75 16.25C10.75 16.5815 10.8817 16.8995 11.1161 17.1339C11.3505 17.3683 11.6685 17.5 12 17.5C12.3315 17.5 12.6495 17.3683 12.8839 17.1339C13.1183 16.8995 13.25 16.5815 13.25 16.25C13.25 15.9185 13.1183 15.6005 12.8839 15.3661C12.6495 15.1317 12.3315 15 12 15Z"
            fill={color}
          />
        </Svg>
      );
    }
    case "settings": {
      const width = size;
      const aspectRatio = 24 / 25;
      const height = width * aspectRatio;

      return (
        <Svg width={width} height={height} viewBox="0 0 24 25" fill="none">
          <Path
            d="M11.8406 15.9469C13.746 15.9469 15.2906 14.4023 15.2906 12.4969C15.2906 10.5915 13.746 9.04688 11.8406 9.04688C9.93524 9.04688 8.39062 10.5915 8.39062 12.4969C8.39062 14.4023 9.93524 15.9469 11.8406 15.9469Z"
            stroke={color}
            strokeWidth={2}
          />
          <Path
            d="M13.8722 1.1748C13.4501 1 12.9142 1 11.8424 1C10.7706 1 10.2347 1 9.81266 1.1748C9.53342 1.29039 9.2797 1.45988 9.066 1.67358C8.85229 1.88728 8.6828 2.14101 8.56721 2.42025C8.46141 2.6767 8.41886 2.97685 8.40276 3.4127C8.3956 3.72779 8.30846 4.03588 8.14955 4.30806C7.99063 4.58023 7.76514 4.80754 7.49426 4.96865C7.21929 5.1228 6.90965 5.20452 6.59443 5.20613C6.2792 5.20774 5.96874 5.12918 5.69221 4.97785C5.30581 4.77315 5.02636 4.66045 4.74921 4.62365C4.14468 4.54415 3.53332 4.70795 3.04951 5.07905C2.68841 5.3585 2.41931 5.82195 1.88341 6.75C1.34751 7.67805 1.07841 8.1415 1.01976 8.59575C0.980243 8.89527 1.00012 9.19964 1.07826 9.49148C1.1564 9.78332 1.29127 10.0569 1.47516 10.2966C1.64536 10.5174 1.88341 10.7025 2.25256 10.9348C2.79651 11.2764 3.14611 11.8583 3.14611 12.5C3.14611 13.1417 2.79651 13.7236 2.25256 14.064C1.88341 14.2974 1.64421 14.4826 1.47516 14.7034C1.29127 14.9431 1.1564 15.2167 1.07826 15.5085C1.00012 15.8004 0.980243 16.1047 1.01976 16.4043C1.07956 16.8574 1.34751 17.3219 1.88226 18.25C2.41931 19.178 2.68726 19.6415 3.04951 19.9209C3.28921 20.1048 3.5628 20.2397 3.85463 20.3179C4.14647 20.396 4.45084 20.4159 4.75036 20.3764C5.02636 20.3396 5.30581 20.2269 5.69221 20.0222C5.96874 19.8708 6.2792 19.7923 6.59443 19.7939C6.90965 19.7955 7.21929 19.8772 7.49426 20.0313C8.04971 20.3533 8.37976 20.9456 8.40276 21.5873C8.41886 22.0243 8.46026 22.3233 8.56721 22.5797C8.6828 22.859 8.85229 23.1127 9.066 23.3264C9.2797 23.5401 9.53342 23.7096 9.81266 23.8252C10.2347 24 10.7706 24 11.8424 24C12.9142 24 13.4501 24 13.8722 23.8252C14.1514 23.7096 14.4051 23.5401 14.6188 23.3264C14.8325 23.1127 15.002 22.859 15.1176 22.5797C15.2234 22.3233 15.266 22.0243 15.2821 21.5873C15.3051 20.9456 15.6351 20.3522 16.1906 20.0313C16.4655 19.8772 16.7752 19.7955 17.0904 19.7939C17.4056 19.7923 17.7161 19.8708 17.9926 20.0222C18.379 20.2269 18.6585 20.3396 18.9345 20.3764C19.234 20.4159 19.5384 20.396 19.8302 20.3179C20.122 20.2397 20.3956 20.1048 20.6353 19.9209C20.9976 19.6426 21.2655 19.178 21.8014 18.25C22.3373 17.3219 22.6064 16.8585 22.6651 16.4043C22.7046 16.1047 22.6847 15.8004 22.6066 15.5085C22.5284 15.2167 22.3936 14.9431 22.2097 14.7034C22.0395 14.4826 21.8014 14.2974 21.4323 14.0651C21.1629 13.9014 20.9395 13.6718 20.7832 13.3981C20.6269 13.1243 20.5428 12.8152 20.5387 12.5C20.5387 11.8583 20.8883 11.2764 21.4323 10.936C21.8014 10.7026 22.0406 10.5174 22.2097 10.2966C22.3936 10.0569 22.5284 9.78332 22.6066 9.49148C22.6847 9.19964 22.7046 8.89527 22.6651 8.59575C22.6053 8.14265 22.3373 7.67805 21.8026 6.75C21.2655 5.82195 20.9976 5.3585 20.6353 5.07905C20.3956 4.89515 20.122 4.76029 19.8302 4.68215C19.5384 4.60401 19.234 4.58413 18.9345 4.62365C18.6585 4.66045 18.379 4.77315 17.9915 4.97785C17.7151 5.12898 17.4048 5.20742 17.0898 5.20581C16.7748 5.2042 16.4654 5.12259 16.1906 4.96865C15.9197 4.80754 15.6942 4.58023 15.5353 4.30806C15.3764 4.03588 15.2892 3.72779 15.2821 3.4127C15.266 2.9757 15.2246 2.6767 15.1176 2.42025C15.002 2.14101 14.8325 1.88728 14.6188 1.67358C14.4051 1.45988 14.1514 1.29039 13.8722 1.1748Z"
            stroke={color}
            strokeWidth={2}
          />
        </Svg>
      );
    }

    case "left-arrow": {
      return (
        <Svg width={size} height={size} viewBox="0 0 26 26" fill="none">
          <Path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M14.1303 20.783C14.0688 20.9311 13.9787 21.0657 13.8652 21.1789C13.752 21.2924 13.6174 21.3825 13.4693 21.444C13.3212 21.5054 13.1625 21.537 13.0021 21.537C12.8418 21.537 12.683 21.5054 12.5349 21.444C12.3868 21.3825 12.2523 21.2924 12.139 21.1789L4.82475 13.8646C4.71123 13.7514 4.62115 13.6169 4.5597 13.4688C4.49824 13.3207 4.46661 13.1619 4.46661 13.0015C4.46661 12.8412 4.49824 12.6824 4.5597 12.5343C4.62115 12.3862 4.71123 12.2517 4.82475 12.1385L12.139 4.82417C12.2524 4.71083 12.3869 4.62092 12.535 4.55958C12.6831 4.49824 12.8418 4.46667 13.0021 4.46667C13.1624 4.46667 13.3211 4.49824 13.4692 4.55958C13.6173 4.62092 13.7519 4.71083 13.8652 4.82417C13.9786 4.93751 14.0685 5.07207 14.1298 5.22016C14.1911 5.36824 14.2227 5.52696 14.2227 5.68725C14.2227 5.84754 14.1911 6.00627 14.1298 6.15435C14.0685 6.30244 13.9786 6.437 13.8652 6.55034L8.63265 11.7809L20.3142 11.7809C20.6375 11.7809 20.9475 11.9094 21.1762 12.138C21.4048 12.3666 21.5332 12.6767 21.5332 13C21.5332 13.3233 21.4048 13.6334 21.1762 13.862C20.9475 14.0906 20.6375 14.219 20.3142 14.219L8.62951 14.219L13.8652 19.4527C13.9787 19.566 14.0688 19.7005 14.1303 19.8486C14.1917 19.9967 14.2233 20.1555 14.2233 20.3158C14.2233 20.4762 14.1917 20.6349 14.1303 20.783Z"
            fill={color}
          />
        </Svg>
      );
    }
    case "down-arrow": {
      const width = size;
      const aspectRatio = 18 / 14;
      const height = width * aspectRatio;

      return (
        <Svg
          width={width}
          height={height}
          viewBox="0 0 14 18"
          fill="none"
          {...props}
        >
          <Path
            d="M6.29363 17.3644C6.48116 17.5519 6.73547 17.6572 7.00063 17.6572C7.2658 17.6572 7.52011 17.5519 7.70763 17.3644L13.3646 11.7074C13.4601 11.6152 13.5363 11.5048 13.5887 11.3828C13.6411 11.2608 13.6687 11.1296 13.6699 10.9968C13.671 10.8641 13.6457 10.7324 13.5955 10.6095C13.5452 10.4866 13.4709 10.3749 13.377 10.281C13.2831 10.1872 13.1715 10.1129 13.0486 10.0626C12.9257 10.0123 12.794 9.98704 12.6612 9.98819C12.5285 9.98934 12.3972 10.0169 12.2752 10.0693C12.1532 10.1217 12.0429 10.1979 11.9506 10.2934L8.00063 14.2434L8.00064 1.00044C8.00064 0.735224 7.89528 0.480869 7.70774 0.293333C7.52021 0.105796 7.26585 0.000440038 7.00064 0.000440014C6.73542 0.000439991 6.48107 0.105796 6.29353 0.293332C6.10599 0.480869 6.00064 0.735223 6.00064 1.00044L6.00063 14.2434L2.05063 10.2934C1.86203 10.1113 1.60943 10.0105 1.34723 10.0128C1.08504 10.015 0.834224 10.1202 0.648816 10.3056C0.463408 10.491 0.358238 10.7418 0.35596 11.004C0.353681 11.2662 0.454475 11.5188 0.636633 11.7074L6.29363 17.3644Z"
            fill="#220010"
          />
        </Svg>
      );
    }

    case "check":
      return (
        <Svg width={size} height={size} viewBox="0 0 56 56" fill="none">
          <Path
            d="M51 21.3638L21 51.3638L6 36.3638"
            stroke={color}
            strokeWidth={5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </Svg>
      );

    case "close":
      return (
        <Svg width={size} height={size} viewBox="0 0 32 32" fill="none">
          <Path
            d="M6.48641 6.8775C6.79827 6.56669 7.221 6.39211 7.66175 6.39211C8.1025 6.39211 8.52524 6.56669 8.83709 6.8775L15.9778 14.0032L23.1186 6.8775C23.3218 6.65903 23.5804 6.49931 23.8671 6.41519C24.1537 6.33107 24.4577 6.32565 24.7472 6.3995C25.0366 6.47335 25.3008 6.62375 25.5117 6.83483C25.7227 7.04592 25.8728 7.30989 25.946 7.59892C26.0199 7.88745 26.0146 8.1905 25.9307 8.47629C25.8468 8.76207 25.6874 9.02007 25.4692 9.22323L18.3285 16.349L25.4692 23.4747C25.6882 23.6775 25.8482 23.9356 25.9325 24.2216C26.0168 24.5076 26.0222 24.811 25.9482 25.0999C25.8742 25.3887 25.7235 25.6523 25.512 25.8628C25.3004 26.0734 25.0359 26.2231 24.7463 26.2962C24.4571 26.3699 24.1535 26.3646 23.8671 26.2809C23.5807 26.1972 23.3221 26.0381 23.1186 25.8204L15.9778 18.6947L8.83709 25.8204C8.52186 26.1139 8.10473 26.2738 7.67364 26.2664C7.24256 26.259 6.83121 26.0848 6.52633 25.7806C6.22147 25.4763 6.04691 25.0659 6.03946 24.6357C6.03202 24.2055 6.19226 23.7892 6.48641 23.4747L13.6271 16.349L6.48641 9.22323C6.17495 8.91204 6 8.49019 6 8.05037C6 7.61054 6.17495 7.1887 6.48641 6.8775Z"
            fill={color}
          />
        </Svg>
      );

    case "checkbox-blank-outline":
      return (
        <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
          <Rect
            x={2.5}
            y={2.53162}
            width={19}
            height={19}
            rx={3.5}
            stroke={color}
          />
        </Svg>
      );
    case "checkbox-marked-outline":
      return (
        <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
          <Rect x={2} y={2.03162} width={20} height={20} rx={4} fill={color} />
          <Path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M16.6062 7.51345L9.60622 14.5601L7.21289 12.1401C7.09289 12.0201 6.93955 11.9601 6.77289 11.9601C6.60622 11.9601 6.45289 12.0268 6.33289 12.1401C6.09289 12.3801 6.08622 12.7735 6.33289 13.0201L9.16621 15.8801C9.39955 16.1134 9.81288 16.1134 10.0462 15.8801L17.4862 8.38678C17.7262 8.14678 17.7262 7.75345 17.4862 7.50678C17.2462 7.26678 16.8529 7.26678 16.6062 7.50678V7.51345Z"
            fill="white"
          />
        </Svg>
      );
  }

  return (
    <MaterialCommunityIcons name={name} color={color} size={size} {...props} />
  );
}
